import {
  AnalyticsActionCategories,
  AnalyticsActionTypes,
} from '@clarity-website/common/Analytics';
import { useAnalytics } from '@clarity-website/common/useAnalytics';
import objectStorageService from '@clarity-website/utils/object-storage-service';
import { useCallback, useMemo, useReducer } from 'react';

export type TransientTableAction = {
  type:
    | 'ADD_TRANSIENT_ID'
    | 'REMOVE_TRANSIENT_ID'
    | 'SET_LOADING'
    | 'CLEAR_LOADING';
  reportTemplateId: string;
  transientId?: string;
  scrollToTable?: (tableId: string) => void;
};

export interface TransientIdsByreportTemplateId {
  [reportTemplateId: string]: string[];
}

export interface TransientIdsBySheetId {
  [sheetId: string]: TransientIdsByreportTemplateId;
}

export interface TransientIdMapping {
  [reportGroupId: string]: TransientIdsBySheetId;
}

const PREFIX = 'transient-id';
const KEY = 'mapping';
const storage = objectStorageService(PREFIX);

export function getTransientIdMappingFromStorage(): TransientIdMapping {
  return storage.getObject<TransientIdMapping>(KEY) || {};
}

export function setTransientIdMappingInStorage(
  mapping: TransientIdMapping,
): void {
  storage.setObject(KEY, mapping);
}

/**
 * Gets transient IDs for a specific report template
 * @param reportGroupId The ID of the report group
 * @param sheetId The ID of the sheet
 * @param reportTemplateId The ID of the report template
 * @returns Array of transient IDs associated with the report template
 */
export function getTransientIdsForReport(
  reportGroupId: string,
  sheetId: string,
  reportTemplateId: string,
): string[] {
  const mapping = getTransientIdMappingFromStorage();
  return mapping[reportGroupId]?.[sheetId]?.[reportTemplateId] || [];
}

/**
 * Adds a transient ID to a report template's mapping
 * @param reportGroupId The ID of the report group
 * @param sheetId The ID of the sheet
 * @param reportTemplateId The ID of the report template
 * @param transientId The transient ID to add
 */
export function addTransientIdToMapping(
  reportGroupId: string,
  sheetId: string,
  reportTemplateId: string,
  transientId: string,
): void {
  const mapping = getTransientIdMappingFromStorage();

  // Initialize nested objects if they don't exist
  if (!mapping[reportGroupId]) {
    mapping[reportGroupId] = {};
  }

  if (!mapping[reportGroupId][sheetId]) {
    mapping[reportGroupId][sheetId] = {};
  }

  if (!mapping[reportGroupId][sheetId][reportTemplateId]) {
    mapping[reportGroupId][sheetId][reportTemplateId] = [];
  }

  if (
    !mapping[reportGroupId][sheetId][reportTemplateId].includes(transientId)
  ) {
    mapping[reportGroupId][sheetId][reportTemplateId].push(transientId);
    setTransientIdMappingInStorage(mapping);
  }
}

/**
 * Removes a transient ID from a report template's mapping
 * @param reportGroupId The ID of the report group
 * @param sheetId The ID of the sheet
 * @param reportTemplateId The ID of the report template
 * @param transientId The transient ID to remove
 */
export function removeTransientIdFromMapping(
  reportGroupId: string,
  sheetId: string,
  reportTemplateId: string,
  transientId: string,
): void {
  const mapping = getTransientIdMappingFromStorage();

  if (mapping[reportGroupId]?.[sheetId]?.[reportTemplateId]) {
    mapping[reportGroupId][sheetId][reportTemplateId] = mapping[reportGroupId][
      sheetId
    ][reportTemplateId].filter((id) => id !== transientId);

    if (mapping[reportGroupId][sheetId][reportTemplateId].length === 0) {
      delete mapping[reportGroupId][sheetId][reportTemplateId];
    }

    if (Object.keys(mapping[reportGroupId][sheetId]).length === 0) {
      delete mapping[reportGroupId][sheetId];
    }

    if (Object.keys(mapping[reportGroupId]).length === 0) {
      delete mapping[reportGroupId];
    }

    setTransientIdMappingInStorage(mapping);
  }
}

const CELL_DRILL_DOWN_PREFIX = 'cell drill-down';

const cellDrillDownSuffixes = {
  CELL_CLICKED: 'clicked',
  REVIEW_ROW_LEVEL: 'review row-level table',
  EASY_MODE: 'easy-mode dropdown',
  ADVANCED_EDITING: 'advanced editing',
  DELETE_TABLE: 'delete table',
  NO_ACCESS: 'no access',
  SUBMIT_SIM: 'submit sim for access',
} as const;

export const CellDrillDownEventCategories = Object.freeze(
  Object.fromEntries(
    Object.entries(cellDrillDownSuffixes).map(([key, suffix]) => [
      key,
      `${CELL_DRILL_DOWN_PREFIX} ${suffix}`,
    ]),
  ),
) as Record<keyof typeof cellDrillDownSuffixes, string>;

/**
 * Custom hook for recording cell drill-down analytics with consistent base attributes
 * @returns A function that accepts custom attributes and records analytics with required base attributes
 */
export function useCellDrillDownAnalytics() {
  const { record } = useAnalytics();

  return useCallback(
    (customAttributes: Record<string, any>) => {
      record({
        name: 'cell-drill-down',
        attributes: {
          type: AnalyticsActionTypes.CLICK,
          actionCategory: AnalyticsActionCategories.CELL_DRILL_DOWN,
          ...customAttributes,
        },
      });
    },
    [record],
  );
}

interface TransientTablesState {
  transientIds: { [reportTemplateId: string]: string[] };
  isLoading: boolean;
}

function transientTablesReducer(
  state: TransientTablesState,
  action: TransientTableAction,
): TransientTablesState {
  switch (action.type) {
    case 'ADD_TRANSIENT_ID': {
      const { reportTemplateId, transientId, scrollToTable } = action;
      if (!transientId) return state;

      const existingIds = state.transientIds[reportTemplateId] || [];

      if (existingIds.includes(transientId)) {
        return state;
      }

      // Scroll to the new transient table after it's added
      if (scrollToTable) {
        // Use setTimeout to ensure the table is rendered before scrolling
        setTimeout(() => scrollToTable(transientId), 100);
      }

      return {
        ...state,
        transientIds: {
          ...state.transientIds,
          [reportTemplateId]: [...existingIds, transientId],
        },
      };
    }
    case 'REMOVE_TRANSIENT_ID': {
      const { reportTemplateId, transientId } = action;
      if (!transientId) return state;

      if (!state.transientIds[reportTemplateId]) {
        return state;
      }

      const updatedIds = state.transientIds[reportTemplateId].filter(
        (id) => id !== transientId,
      );

      if (updatedIds.length === 0) {
        const newTransientIds = { ...state.transientIds };
        delete newTransientIds[reportTemplateId];
        return {
          ...state,
          transientIds: newTransientIds,
        };
      }

      return {
        ...state,
        transientIds: {
          ...state.transientIds,
          [reportTemplateId]: updatedIds,
        },
      };
    }
    case 'SET_LOADING': {
      return {
        ...state,
        isLoading: true,
      };
    }
    case 'CLEAR_LOADING': {
      return {
        ...state,
        isLoading: false,
      };
    }
    default:
      return state;
  }
}

/**
 * Interface for the sheet structure in the GQL format
 */
export interface TransientMappingSheet {
  id: string;
  reportTemplates: {
    id: string;
    transientIds: string[];
  }[];
}

/**
 * Interface for the GQL format of transient ID mapping
 */
export interface TransientMappingGQL {
  sheets: TransientMappingSheet[];
}

export function useTransientIdMapping(reportGroupId: string, sheetId?: string) {
  const getFullMapping = useCallback(() => {
    const mapping = getTransientIdMappingFromStorage();
    return mapping[reportGroupId] || {};
  }, [reportGroupId]);

  // Calculate the initial transient state
  const initialTransientState = useMemo((): TransientTablesState => {
    const fullMapping = getFullMapping();
    const sheetMapping = sheetId ? fullMapping[sheetId] || {} : {};
    const transientIds: { [reportTemplateId: string]: string[] } = {};

    // Map reportTemplateId -> [transientId] in the state
    Object.entries(sheetMapping).forEach(
      ([reportTemplateId, transientIdArray]) => {
        transientIds[reportTemplateId] = transientIdArray;
      },
    );

    return {
      transientIds,
      isLoading: false,
    };
  }, [getFullMapping, sheetId]);

  const [transientTablesState, transientTablesDispatch] = useReducer(
    transientTablesReducer,
    initialTransientState,
  );

  /**
   * Finds the source report template ID for a given transient ID
   * @param sheetId The ID of the sheet
   * @param transientId The transient ID to find the source for
   * @returns The source report template ID or undefined if not found
   */
  const findSourceReportTemplateId = useCallback(
    (sheetId: string, transientId: string): string | undefined => {
      const mapping = getTransientIdMappingFromStorage();

      if (!mapping[reportGroupId]?.[sheetId]) {
        return undefined;
      }

      const reportTemplates = mapping[reportGroupId][sheetId];
      const entry = Object.entries(reportTemplates).find(([_, transientIds]) =>
        transientIds.includes(transientId),
      );

      return entry ? entry[0] : undefined;
    },
    [reportGroupId],
  );

  /**
   * Gets the transient ID mapping in a format suitable for GQL
   * @returns The transient ID mapping in GQL format
   */
  const getGQLMapping = useCallback((): TransientMappingGQL => {
    const fullMapping = getFullMapping();
    const sheets: TransientMappingSheet[] = [];

    // Transform the mapping into the desired format
    Object.entries(fullMapping).forEach(([sheetId, reportTemplateMap]) => {
      const reportTemplates = Object.entries(reportTemplateMap).map(
        ([reportTemplateId, transientIds]) => ({
          id: reportTemplateId,
          transientIds,
        }),
      );

      if (reportTemplates.length > 0) {
        sheets.push({
          id: sheetId,
          reportTemplates,
        });
      }
    });

    return { sheets };
  }, [getFullMapping]);

  /**
   * Gets the total count of transient tables for a given reportGroupId and sheetId
   * @param reportGroupId The ID of the report group
   * @param sheetId The ID of the sheet
   * @returns The total count of transient tables
   */
  const getTransientTableCount = useCallback(
    (sheetId: string): number => {
      const fullMapping = getFullMapping();

      if (!fullMapping[sheetId]) {
        return 0;
      }

      const reportTemplateMap = fullMapping[sheetId];
      let totalCount = 0;

      // Sum up all transient IDs across all report templates in the sheet
      Object.values(reportTemplateMap).forEach((transientIds) => {
        totalCount += transientIds.length;
      });

      return totalCount;
    },
    [getFullMapping],
  );

  /**
   * Clears all transient IDs for the current report group
   * This removes all transient tables from local storage for this report group
   */
  const clearAllTransientIds = useCallback(() => {
    const mapping = getTransientIdMappingFromStorage();
    delete mapping[reportGroupId];
    setTransientIdMappingInStorage(mapping);
  }, [reportGroupId]);

  return {
    getTransientIdsForReport: (sheetId: string, reportTemplateId: string) =>
      getTransientIdsForReport(reportGroupId, sheetId, reportTemplateId),

    addTransientId: (
      sheetId: string,
      reportTemplateId: string,
      transientId: string,
    ) =>
      addTransientIdToMapping(
        reportGroupId,
        sheetId,
        reportTemplateId,
        transientId,
      ),

    removeTransientId: (
      sheetId: string,
      reportTemplateId: string,
      transientId: string,
    ) =>
      removeTransientIdFromMapping(
        reportGroupId,
        sheetId,
        reportTemplateId,
        transientId,
      ),

    findSourceReportTemplateId,
    getFullMapping,
    getGQLMapping,
    getTransientTableCount,
    clearAllTransientIds,
    initialTransientState,
    transientTablesMap: transientTablesState.transientIds,
    transientTablesDispatch,
    isLoading: transientTablesState.isLoading,
  };
}
